/* eslint-disable parog-web/no-glint-expect-error-directive */
import { tracked } from '@glimmer/tracking';
import type { EmberChangeset } from 'ember-changeset';
import add from 'ember-math-helpers/helpers/add';
import ThymicEpisodeComponent from 'parog-web/components/disorder-history/thymic-episode';
import type {
  ForcModelPojo,
  Payload as ForcPayload
} from 'parog-web/components/forc/abstract/journal-event-based-component';
import JournalEventBasedComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import journalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import ModelList from 'parog-web/components/model-list';
import DisorderHistoryDeclaredJournalEvent from 'parog-web/models/disorder-history-declared-journal-event';

interface ThymicEpisode {
  occurredAt?: Date;
}

interface DisorderHistoryModelType {
  thymicEpisodes: ThymicEpisode[];
  firstThymicEpisode?: ThymicEpisode;
}

interface DisorderHistoryPayloadType {
  thymicEpisodes: ThymicEpisode[];
  firstThymicEpisode?: ThymicEpisode;
}

type DisorderHistoryEditionFormModel = ForcModelPojo & DisorderHistoryModelType;
type DisorderHistoryEditionFormPayload = ForcPayload &
  DisorderHistoryPayloadType;

export default class DisorderHistoryEditionFormComponent extends JournalEventBasedComponent {
  static override forcDisplayName =
    'Historique du trouble avant accompagnement';
  static override fullComponentId = 'disorder-history/edition';
  static override journalEventClass = DisorderHistoryDeclaredJournalEvent;

  /**
   * Store the sub changesets created for the thymic episodes.
   */
  @tracked private thymicEpisodeChangesets: EmberChangeset[] = [];

  override journalEventProperties = [
    'occurredAt',
    'numberOfDepressiveEpisode',
    'numberOfHypomaniacEpisode',
    'numberOfManiacEpisode',
    'numberOfEpisodeCommentary',
    'numberOfHospitalization',
    'numberOfTs',
    'numberOfTsWithThymicEpisode',
    'numberOfTsCommentary',
    'presenceOfRapidCycles',
    'presenceOfRapidCyclesCommentary',
    'thymicEpisodes',
    'firstThymicEpisode'
  ];
  override retrieveJournalEvent =
    journalEventRetrievalStrategies.sourceJournalEvent;

  override get isDirty(): boolean {
    if (this.changeset.get('isDirty')) {
      return true;
    }
    for (const changeset of this.thymicEpisodeChangesets) {
      if (changeset.get('isDirty')) {
        return true;
      }
    }

    return false;
  }

  override computePayload(): ForcPayload {
    const payload = super.computePayload() as DisorderHistoryEditionFormPayload;
    // Execute every sub-changeset to apply changes to our model
    this.thymicEpisodeChangesets.forEach(changeset => changeset.execute());
    // Save the first thymic episode
    if (payload.firstThymicEpisode) {
      payload.thymicEpisodes.push(payload.firstThymicEpisode);
    }

    return payload;
  }

  onThymicEpisodeCreated = (changeset: EmberChangeset): void => {
    this.thymicEpisodeChangesets.push(changeset);
  };

  onThymicEpisodeDestroyed = (changeset: EmberChangeset): void => {
    this.thymicEpisodeChangesets = this.thymicEpisodeChangesets.filter(
      e => e != changeset
    );
  };

  override retrieveModel(): DisorderHistoryEditionFormModel {
    const model = super.retrieveModel() as DisorderHistoryEditionFormModel;

    if (this.isNewMode) {
      model.thymicEpisodes = [];
      model.firstThymicEpisode = {};
      model.occurredAt = new Date();
    } else {
      // Extract the first thymic episode
      model.firstThymicEpisode = this.retrieveFirstThymicEpisode(
        model.thymicEpisodes
      );
      model.thymicEpisodes = model.thymicEpisodes.filter(
        episode => episode != model.firstThymicEpisode
      );
    }

    return model;
  }

  private retrieveFirstThymicEpisode(
    thymicEpisodes: ThymicEpisode[]
  ): ThymicEpisode | undefined {
    return thymicEpisodes.slice().sort((a, b) => {
      return (
        new Date(a.occurredAt as Date).getTime() -
        new Date(b.occurredAt as Date).getTime()
      );
    })[0];
  }

  <template>
    <Provider @forcComponent={{this}} @patient={{@patient}}>
      <:content as |Form|>
        <h4>
          Les informations renseignées ici décrivent le trouble du patient avant
          le début de son accompagnement dans le programme.<br />
          Les épisodes qui pourraient survenir au cours de l'accompagnement sont
          déclarés et suivis dans un espace dédié de la Surveillance médicale.
        </h4>
        <AccordionBlock @title='Évaluation vie entière' @isActive={{true}}>
          <div class='fields'>
            <div class='six wide field'>
              <Form.NumberInput
                @labelName="Nombre d'épisode dépressif"
                @propertyName='numberOfDepressiveEpisode'
                @type='integer'
                @min='0'
              />
            </div>

            <div class='six wide field'>
              <Form.NumberInput
                @labelName="Nombre d'épisode hypomaniaque"
                @propertyName='numberOfHypomaniacEpisode'
                @type='integer'
                @min='0'
              />
            </div>

            <div class='six wide field'>
              <Form.NumberInput
                @labelName="Nombre d'épisode maniaque"
                @propertyName='numberOfManiacEpisode'
                @type='integer'
                @min='0'
              />
            </div>
          </div>

          <div class='field'>
            <Form.TextArea
              @labelName='Précisions'
              @propertyName='numberOfEpisodeCommentary'
              @rows={{2}}
            />
          </div>

          <div class='ui section divider hidden'></div>

          <div class='fields'>
            <div class='six wide field'>
              <Form.NumberInput
                @labelName="Nombre d'hospitalisation"
                @propertyName='numberOfHospitalization'
                @type='integer'
                @min='0'
              />
            </div>

            <div class='six wide field'>
              <Form.NumberInput
                @labelName='Nombre de TS'
                @propertyName='numberOfTs'
                @type='integer'
                @min='0'
              />
            </div>
          </div>

          <div class='fields'>
            <div class='seven wide field'>
              <Form.NumberInput
                @labelName='Nombre de TS associée à un épisode thymique'
                @propertyName='numberOfTsWithThymicEpisode'
                @type='integer'
                @min='0'
              />
            </div>
          </div>

          <div class='field'>
            <Form.TextArea
              @labelName='Précisions'
              @propertyName='numberOfTsCommentary'
              @rows={{2}}
            />
          </div>

          <div class='ui section divider hidden'></div>

          <div class='fields'>
            <div class='six wide field'>
              <Form.AlignedFieldsRadioGroup
                @propertyName='presenceOfRapidCycles'
                @title='Présence de cycles rapides actuelle'
                as |Group|
              >
                <Group.FieldRadio @label='Oui' @value='yes' />
                <Group.FieldRadio @label='Non' @value='no' />
              </Form.AlignedFieldsRadioGroup>
            </div>

            <div class='twelve wide field'>
              <Form.TextArea
                @labelName='Précisions'
                @propertyName='presenceOfRapidCyclesCommentary'
                @rows={{1}}
              />
            </div>
          </div>
        </AccordionBlock>

        <AccordionBlock
          @title='Focus sur le premier épisode thymique'
          @isActive={{true}}
        >
          {{! @glint-expect-error }}
          <ThymicEpisodeComponent
            @patient={{@patient}}
            {{! @glint-expect-error }}
            @model={{this.model.firstThymicEpisode}}
            @onChangesetCreated={{this.onThymicEpisodeCreated}}
            @onChangesetDestroyed={{this.onThymicEpisodeDestroyed}}
            @index='1'
            data-test-thymic-episode='1'
          />
        </AccordionBlock>

        <AccordionBlock
          @title='Focus sur les 3 dernières années'
          @isActive={{false}}
        >
          <ModelList
            @model={{this.changeset}}
            @addButtonTitle='Ajouter un épisode thymique'
            @propertyName='thymicEpisodes'
            as |Item|
          >
            {{! @glint-expect-error }}
            <ThymicEpisodeComponent
              @patient={{@patient}}
              @model={{Item.item}}
              @index={{add Item.index 2}}
              @onDestroy={{Item.destroy}}
              @onChangesetCreated={{this.onThymicEpisodeCreated}}
              @onChangesetDestroyed={{this.onThymicEpisodeDestroyed}}
              data-test-thymic-episode={{add Item.index 2}}
            />
          </ModelList>
        </AccordionBlock>
      </:content>
    </Provider>
  </template>
}
